import React, { useEffect, useRef, useState } from "react";
import Fuse from "fuse.js";

// Each FAQ entry includes a `keywords` field with synonyms to improve Fuse.js matching.
// Swap `answer` values for real content when client provides it.
const FAQS = [
  {
    question: "Do you come to my location?",
    keywords: "mobile come location home driveway workplace visit travel",
    answer: "Yes — 100% mobile. We come to your driveway, workplace, or wherever your car is. No drop-off needed.",
  },
  {
    question: "How much does it cost?",
    keywords: "price cost how much charge fee pricing rates expensive quote estimate",
    answer: "Our Standard Detail starts from $189, Deluxe Detail from $248, Factory Detail from $379, and Ceramic Coating from $889. Use the quote calculator on this page to get a ballpark estimate based on your vehicle and condition.",
  },
  {
    question: "How long does a detail take?",
    keywords: "long time duration hours quick fast how long appointment",
    answer: "A Standard Detail typically takes 2–3 hours. The Deluxe Detail runs 3–5 hours. Our Factory Detail is a full-day job. Ceramic Coating with paint correction takes 1–2 full days depending on the vehicle.",
  },
  {
    question: "What areas do you serve?",
    keywords: "area location serve region coverage zip near me PA NJ OH Ohio Pennsylvania Jersey",
    answer: "We cover 3 regions: 2hrs from Levittown PA, 1hr from JBMDL NJ, and 1hr from Lima OH. Check the service area map on this page.",
  },
  {
    question: "How do I book an appointment?",
    keywords: "book schedule appointment reserve calendar how to get started",
    answer: "Hit the Book Now button at the top of the page to pick your service and time through Square. You'll get a confirmation and reminders automatically.",
  },
  {
    question: "What payment methods do you accept?",
    keywords: "pay payment cash card credit venmo apple pay google pay methods accepted",
    answer: "We accept all major cards, Apple Pay, Google Pay, and cash — processed through Square at time of service.",
  },
  {
    question: "Do I need to be home?",
    keywords: "home present stay leave go work be there supervise",
    answer: "Nope. As long as we have access to the vehicle, you're free to carry on with your day.",
  },
  {
    question: "What is ceramic coating?",
    keywords: "ceramic coating what is how does it work protection hydrophobic worth it",
    answer: "Ceramic coating is a liquid polymer that bonds to your paint and creates a hard, hydrophobic layer. It repels water, UV, and minor scratches — and makes washing much easier. Our ceramic coating comes with a 5-year protection warranty and paint correction is included.",
  },
  {
    question: "Are you licensed and insured?",
    keywords: "licensed insured insurance trust safe legitimate bonded",
    answer: "Yes — fully licensed and insured on every job. Your vehicle is protected.",
  },
  {
    question: "How often should I get a detail?",
    keywords: "often frequency how many times regular maintenance schedule",
    answer: "Every 3–6 months for daily drivers. Ceramic-coated vehicles can go longer between details since the coating repels contamination.",
  },
];

const fuse = new Fuse(FAQS, {
  keys: [
    { name: "question", weight: 0.6 },
    { name: "keywords", weight: 0.4 },
  ],
  threshold: 0.45,
  minMatchCharLength: 3,
});

interface Message {
  role: "user" | "bot";
  text: string;
  isLink?: boolean;
}

const GREETING: Message = {
  role: "bot",
  text: "Hey! I'm Bizzy's assistant. Ask me anything about our services, pricing, or booking — I'll point you in the right direction.",
};

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([GREETING]);
  const [input, setInput] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const send = () => {
    const text = input.trim();
    if (!text) return;
    setInput("");

    const userMsg: Message = { role: "user", text };
    const results = fuse.search(text);

    const botMsg: Message =
      results.length > 0
        ? { role: "bot", text: results[0]!.item.answer }
        : {
            role: "bot",
            text: "Great question! I don't have that specific information available at the moment, but our detailing specialists at Bizzy Details would be happy to assist you. Please contact us directly, and we'll make sure you get the answers you need.",
            isLink: true,
          };

    setMessages((prev) => [...prev, userMsg, botMsg]);
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Open chat"
        className="fixed bottom-6 right-6 z-[300] flex items-center justify-center w-14 h-14 rounded-full shadow-2xl transition-transform hover:scale-105"
        style={{ background: "#1E5EFF", boxShadow: "0 0 32px rgba(30,94,255,0.5)" }}
      >
        {open ? (
          <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={1.6}>
            {/* antenna */}
            <path strokeLinecap="round" d="M12 2v3" />
            <circle cx="12" cy="1.5" r="1" fill="white" stroke="none" />
            {/* head */}
            <rect x="3" y="5" width="18" height="14" rx="2.5" />
            {/* eyes */}
            <rect x="7.5" y="9.5" width="2.75" height="2.75" rx="0.5" fill="white" stroke="none" />
            <rect x="13.75" y="9.5" width="2.75" height="2.75" rx="0.5" fill="white" stroke="none" />
            {/* smile */}
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.5 15.5q3.5 2 7 0" />
          </svg>
        ) : (
          <svg width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={1.75}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75v4.5a2.25 2.25 0 002.25 2.25h1.5a2.25 2.25 0 002.25-2.25v-2.25a2.25 2.25 0 00-2.25-2.25H4.5V12a7.5 7.5 0 0115 0v2.25h-1.5a2.25 2.25 0 00-2.25 2.25v2.25a2.25 2.25 0 002.25 2.25h1.5a2.25 2.25 0 002.25-2.25V12c0-5.385-4.365-9.75-9.75-9.75z" />
          </svg>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-[300] flex flex-col rounded-none shadow-2xl overflow-hidden"
          style={{
            width: 340,
            height: 460,
            background: "#0F0F0F",
            border: "1px solid #252525",
            boxShadow: "0 0 48px rgba(0,0,0,0.8)",
          }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3" style={{ background: "#1E5EFF" }}>
            <img src="/logo-clean.png" alt="Bizzy" style={{ height: 32, width: "auto" }} />
            <div>
              <div className="font-black italic uppercase text-white" style={{ fontFamily: "var(--font-display)", fontSize: "0.9rem" }}>
                Bizzy Assistant
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: "rgba(255,255,255,0.5)" }} />
                <span className="font-medium" style={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.7)" }}>Ask me anything</span>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3" style={{ scrollbarWidth: "none" }}>
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className="max-w-[80%] px-4 py-2.5 text-sm leading-relaxed"
                  style={{
                    borderRadius: 2,
                    background: m.role === "user" ? "#1E5EFF" : "#1a1a1a",
                    color: m.role === "user" ? "#fff" : "#F2F2F0",
                    fontWeight: m.role === "user" ? 600 : 400,
                  }}
                >
                  {m.text}
                  {m.isLink && (
                    <a
                      href="#quote"
                      className="block mt-2 font-bold uppercase text-xs tracking-widest"
                      style={{ color: "#1E5EFF", fontFamily: "var(--font-display)" }}
                      onClick={() => setOpen(false)}
                    >
                      Send us a message →
                    </a>
                  )}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="flex gap-2 px-3 py-3" style={{ borderTop: "1px solid #1a1a1a" }}>
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask a question..."
              className="flex-1 bg-transparent text-sm outline-none"
              style={{ color: "#F2F2F0", caretColor: "#1E5EFF" }}
            />
            <button
              onClick={send}
              disabled={!input.trim()}
              className="flex-shrink-0 w-8 h-8 flex items-center justify-center transition-opacity disabled:opacity-30"
              style={{ background: "#1E5EFF", borderRadius: 2 }}
            >
              <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
